import { createBrowserRouter, Navigate, useParams } from "react-router";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import {
  AccountRoute,
  ActivityRoute,
  AdminHomeRoute,
  FinanceDashboardRoute,
  GigDashboardRoute,
  PlatformsRoute,
  RosterRoute,
  TeamRoute,
} from "./routes/AdminRoutes";
import {
  AdminLoginRoute,
  ForgotPasswordRoute,
  PickTrackRoute,
  ResetPasswordRoute,
  SignInRoute,
} from "./routes/AuthRoutes";
import { CertificateRoute } from "./routes/CertificateRoutes";
import { GigBoardRoute, ReportWinRoute } from "./routes/GigBoardRoutes";
import { GigSourcesRoute, InsightsRoute, WinsRoute } from "./routes/GigAdminRoutes";
import { FinanceStepRoute } from "./routes/FinanceRoutes";
import { HubRoute } from "./routes/HubRoute";
import { LaunchPlanRoute, PaidReadyRoute } from "./routes/PaidReadyRoutes";
import { BidRoute, JobBoardRoute } from "./routes/PitchRoutes";
import { PackageRoute, PositionRoute } from "./routes/StepRoutes12";
import { RequireAdmin, RequireParticipant } from "./routes/guards";
import { SimulatedStep } from "./routes/SimulatedStep";
import { AppLayout } from "../components/AppLayout";
import type { UserRole } from "../api/types";

/*
 * Routes are named after what the participant is doing. Every route reads from the API; fixtures live under
 * `tests/` and cannot be imported from here, so a screen showing invented data is a structural impossibility.
 *
 * The step URLs are deep-linkable, but the server decides whether a step is open: a locked step's first API call
 * comes back FORBIDDEN and the screen says so.
 */
const GIG_STEPS: Record<string, () => React.ReactElement> = {
  "1": () => <PositionRoute />,
  "2": () => <PackageRoute />,
  "3": () => <JobBoardRoute />,
  "4": () => <PaidReadyRoute />,
  "5": () => <CertificateRoute />,
  "6": () => <GigBoardRoute />,
};

function GigStepRoute() {
  const { step = "" } = useParams();
  const render = GIG_STEPS[step];
  return render ? render() : <Navigate to="/hub" replace />;
}

const participant = (element: React.ReactElement, needsTrack = true) => (
  <RequireParticipant needsTrack={needsTrack}>{element}</RequireParticipant>
);
const gigAdmin = (element: React.ReactElement) => (
  <RequireAdmin roles={["SuperAdmin", "GigAdmin"]}>{element}</RequireAdmin>
);
/* MERL reads the gig and finance dashboards but changes nothing (except marking wins checked). */
const gigReader = (element: React.ReactElement) => (
  <RequireAdmin roles={["SuperAdmin", "GigAdmin", "Merl"]}>{element}</RequireAdmin>
);
const financeAdmin = (element: React.ReactElement) => (
  <RequireAdmin roles={["SuperAdmin", "FinanceAdmin", "Merl"]}>{element}</RequireAdmin>
);
const ALL_ADMINS: UserRole[] = ["SuperAdmin", "GigAdmin", "FinanceAdmin", "Merl"];
const anyAdmin = (element: React.ReactElement) => (
  <RequireAdmin roles={ALL_ADMINS}>{element}</RequireAdmin>
);

export const routes = [
  { path: "/", element: <SignInRoute /> },
  { path: "/track", element: participant(<PickTrackRoute />, false) },
  { path: "/hub", element: participant(<HubRoute />) },

  {
    path: "/gig/:step",
    element: participant(
      <SimulatedStep pathway="gig">
        <GigStepRoute />
      </SimulatedStep>,
    ),
  },
  { path: "/gig/3/jobs/:code", element: participant(<BidRoute />) },
  { path: "/gig/6/win", element: participant(<ReportWinRoute />) },
  { path: "/launch-plan", element: participant(<LaunchPlanRoute />) },

  {
    path: "/fin/:step",
    element: participant(
      <SimulatedStep pathway="finance">
        <FinanceStepRoute />
      </SimulatedStep>,
      false,
    ),
  },

  { path: "/admin", element: <AdminLoginRoute /> },
  { path: "/admin/forgot-password", element: <ForgotPasswordRoute /> },
  { path: "/admin/reset-password", element: <ResetPasswordRoute /> },
  { path: "/admin/home", element: anyAdmin(<AdminHomeRoute />) },
  {
    path: "/admin/account",
    element: (
      <RequireAdmin roles={ALL_ADMINS} allowPasswordChange>
        <AccountRoute />
      </RequireAdmin>
    ),
  },
  { path: "/admin/activity", element: anyAdmin(<ActivityRoute />) },
  { path: "/admin/team", element: anyAdmin(<TeamRoute />) },
  { path: "/admin/gig", element: gigReader(<GigDashboardRoute />) },
  { path: "/admin/wins", element: gigReader(<WinsRoute />) },
  { path: "/admin/insights", element: gigReader(<InsightsRoute />) },
  { path: "/admin/gig-sources", element: gigReader(<GigSourcesRoute />) },
  { path: "/admin/platforms", element: gigAdmin(<PlatformsRoute />) },
  { path: "/admin/roster", element: gigAdmin(<RosterRoute />) },
  { path: "/admin/finance", element: financeAdmin(<FinanceDashboardRoute />) },

  {
    path: "*",
    element: (
      <AppLayout>
        <NotFoundScreen />
      </AppLayout>
    ),
  },
];

export const router = createBrowserRouter(routes);
